import React from "react";
import { useTranslation } from "react-i18next";
import { CalendarItem } from "../../features/calendar/models/CalendarItem";
import { ClosedDay } from "../../features/closed-days/models/ClosedDay";
import {
  addDays,
  getMonthGridDays,
  groupItemsByDate,
  isSameDay,
  startOfWeek,
  toYmd,
  WeekStartDay,
} from "../../features/calendar/utils/calendar-range";
import { OccurrenceDrag, occurrenceKey } from "../../features/calendar/models/OccurrenceDrag";
import CalendarItemChip from "./CalendarItemChip";
import CalendarDayMenu from "./CalendarDayMenu";

const MAX_CHIPS_PER_CELL = 3;

interface CalendarMonthViewProps {
  anchor: Date;
  items: CalendarItem[];
  closedByDate: Map<string, ClosedDay>;
  /** Declared member absences per day — renders a small display-only count. */
  absenceCountByDate?: Map<string, number>;
  drag: OccurrenceDrag;
  onManageDay: (day: Date, closed?: ClosedDay) => void;
  onCreateSession: (day: Date) => void;
  /** Invoked when the instructor drills into a day with more items than the cell shows. */
  onDayDrillDown: (day: Date) => void;
  weekStart: WeekStartDay;
}

/** Classic month grid: whole weeks, week-start first, leading/trailing days dimmed. */
const CalendarMonthView: React.FC<CalendarMonthViewProps> = ({
  anchor,
  items,
  closedByDate,
  absenceCountByDate,
  drag,
  onManageDay,
  onCreateSession,
  onDayDrillDown,
  weekStart,
}) => {
  const { t, i18n } = useTranslation();
  const days = getMonthGridDays(anchor, weekStart);
  const byDate = groupItemsByDate(items);
  const today = new Date();
  const weekdayFormat = new Intl.DateTimeFormat(i18n.language, { weekday: "short" });
  const weekdayLabels = Array.from({ length: 7 }, (_, i) =>
    weekdayFormat.format(addDays(startOfWeek(today, weekStart), i))
  );
  // The cell only renders the bare day-of-month digit, which repeats across
  // adjacent months shown in the same grid — the trigger needs a full,
  // unambiguous accessible name instead.
  const fullDateFormat = new Intl.DateTimeFormat(i18n.language, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="with-border overflow-hidden rounded-2xl">
      <div className="grid grid-cols-7 border-b border-grey-70 dark:border-grey-50">
        {weekdayLabels.map((label) => (
          <div key={label} className="p-2 text-center text-sm text-grey-40 dark:text-grey-70">
            {label}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const ymd = toYmd(day);
          const dayItems = byDate.get(ymd) ?? [];
          const closed = closedByDate.get(ymd);
          const awayCount = absenceCountByDate?.get(ymd) ?? 0;
          const isToday = isSameDay(day, today);
          const inMonth = day.getMonth() === anchor.getMonth();
          const overflow = dayItems.length - MAX_CHIPS_PER_CELL;
          return (
            <div
              key={ymd}
              onDragOver={(event) => drag.handleDayDragOver(day, event)}
              onDragLeave={() => drag.handleDayDragLeave(day)}
              onDrop={(event) => drag.handleDayDrop(day, event)}
              className={`min-h-24 border-t border-grey-70 p-1 dark:border-grey-50 lg:min-h-28 ${
                inMonth ? "" : "opacity-40"
              } ${closed ? "bg-grey-95 dark:bg-primary-20" : ""} ${
                drag.dragOverYmd === ymd ? "bg-primary-95 dark:bg-[#3E334D]" : ""
              }`}
            >
              <CalendarDayMenu
                day={day}
                closed={closed}
                onManageDay={onManageDay}
                onCreateSession={onCreateSession}
                triggerClassName="mb-1"
                ariaLabel={
                  closed
                    ? `${fullDateFormat.format(day)} — ${t("calendar.closedDay.badge")}`
                    : fullDateFormat.format(day)
                }
              >
                <span
                  className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                    isToday ? "bg-primary-40 text-white" : "text-grey-20 dark:text-white"
                  }`}
                >
                  {day.getDate()}
                </span>
                {closed && (
                  <span className="ms-auto pe-1 text-xs font-medium text-grey-40 dark:text-grey-60">
                    {t("calendar.closedDay.badge")}
                  </span>
                )}
              </CalendarDayMenu>
              {awayCount > 0 && (
                <p className="px-1 text-[10px] font-medium text-grey-40 dark:text-grey-60">
                  {t("absences.awayCount", "{{count}} away", { count: awayCount })}
                </p>
              )}
              <div className="space-y-0.5">
                {dayItems.slice(0, MAX_CHIPS_PER_CELL).map((item, index) => (
                  <CalendarItemChip
                    key={`${item.bookingId}-${item.date}-${index}`}
                    item={item}
                    compact
                    draggable={drag.canDrag(item)}
                    isDragging={drag.draggingKey === occurrenceKey(item)}
                    onDragStart={(event) => drag.handleDragStart(item, event)}
                    onDragEnd={drag.handleDragEnd}
                  />
                ))}
                {overflow > 0 && (
                  <button
                    type="button"
                    onClick={() => onDayDrillDown(day)}
                    className="block w-full cursor-pointer px-2 text-left text-xs text-primary-40 hover:underline dark:text-primary-90"
                  >
                    {t("calendar.more", { count: overflow })}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CalendarMonthView;
